import { useCallback, useEffect, useState } from "react";
import { Breadcrumb, Card, Col, Container, Row } from "react-bootstrap";
import { Helmet } from "react-helmet";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BdaModelDTO } from "../../models/BdaModel";
import AlidaLoader from "../../components/common/AlidaLoader";
import { Alert } from "antd";
import AssetEditForm from "../../components/forms/AssetEditForm";
import { useToasts } from "../../contexts/toasts";
import { Models } from "../../api/Models";
import { useAuth } from "../../contexts/auth";
import { useFullScreenLoader } from "../../contexts/fullScreenLoader";
import { Authentication } from "../../api/Authentication";
import Cookies from "js-cookie";
import { useSelector } from "react-redux";
import { sectionLabelsSelector } from "../../slices/sectionsSlice";
import { RootState } from "../../store";

export default function ModelEdit() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { showLoader, hideLoader } = useFullScreenLoader();
  const [model, setModel] = useState<BdaModelDTO>();
  const [error, setError] = useState(false);

  const { showToast } = useToasts();
  const { setToken } = useAuth();

  const modelLabels = useSelector((state: RootState) => sectionLabelsSelector(state, "models"));

  const retrieveModel = useCallback(() => {
    Models.getModel(+id!)
      .then(
        ({ data }) => setModel(data),
        (err) => {
          setError(true);
          const message = err.response?.data?.message || err.message;
          const status = err.response?.status;
          showToast('models-error', message, `${modelLabels.plural} Error ${status ? `(${status})` : ''}`, 'danger');
        }
      );
  }, [id, showToast, modelLabels]);

  const onCancel = useCallback(() => navigate('/models'), [navigate]);

  const submitModel = useCallback((body: BdaModelDTO) => {
    showLoader();

    const promise = id ? Models.updateModel(body) : Models.createModel(body);
    promise.then(
      ({ data }) => {
        hideLoader();
        showToast('create-model-success', `${modelLabels.singular} ${id ? 'updated' : 'created'} successfully`, `${modelLabels.singular} ${id ? 'Updated' : 'Created'}`, 'success');
        if (!id) {
          Authentication.refreshToken()
            .then(
              ({ data: tokenInfo }) => {
                setToken(tokenInfo.access_token);
                Cookies.set(window.__env.AUTH_TOKEN_NAME || "alida-token", tokenInfo.access_token, { expires: tokenInfo.expires_in });
                Cookies.set(window.__env.AUTH_REFRESH_TOKEN_NAME || "alida-refresh-token", tokenInfo.refresh_token, { expires: tokenInfo.refresh_expires_in });
                navigate(`/models/${data.id}`);
              },
              (err) => {
                const message = err.response?.data?.message || err.message;
                const status = err.response?.status;
                console.log("Error refreshing token:", status, message);
                navigate(`/models/${data.id}`);
                // showToast('refresh-token-error', message, `Refresh Token Error ${status ? `(${status})` : ''}`, 'danger');
              }
            )
        }
        else navigate(`/models/${data.id}`);
      },
      (err) => {
        hideLoader();
        const message = err.response?.data?.message || err.message;
        const status = err.response?.status;
        showToast('file-upload-error', message, `${modelLabels.singular} ${id ? 'Update' : 'Creation'} Error ${status ? `(${status})` : ''}`, 'danger');
      }
    )
  }, [id, showLoader, hideLoader, navigate, setToken, showToast, modelLabels]);

  useEffect(() => {
    if (id) retrieveModel();
    else setModel(new BdaModelDTO());
  }, [id, retrieveModel]);

  const renderAsyncContent = useCallback(() => {
    if (error) return (
      <Alert
        type="error"
        showIcon
        message={`An error occurred while retrieving ${modelLabels.singular}. Try again later.`}
      />
    );

    return model
      ? <AssetEditForm asset={model} mode="model" modeLabel={modelLabels.singular || "Model"} onCancel={onCancel} onSubmit={submitModel} />
      : (
        <div className="p-5 text-center">
          <AlidaLoader />
        </div>
      );
  }, [error, model, submitModel, onCancel, modelLabels]);

  return (
    <Container className="mt-3">
      <Helmet>
        <title>{modelLabels.singular} {id ? `${id} Edit` : "Registration"}</title>
      </Helmet>

      <Breadcrumb>
        <Breadcrumb.Item linkAs={Link} linkProps={{ to: '/models' }}>{modelLabels.plural}</Breadcrumb.Item>
        {id
          ? (
            <>
              <Breadcrumb.Item linkAs={Link} linkProps={{ to: `/models/${id}` }}>{model?.name}</Breadcrumb.Item>
              <Breadcrumb.Item active>Edit</Breadcrumb.Item>
            </>
          )
          : (
            <Breadcrumb.Item active>New {modelLabels.singular}</Breadcrumb.Item>
          )}
      </Breadcrumb>

      <Card className="mb-3">
        <Row className="gx-0 border-bottom p-2 px-3 align-items-center">
          <Col>
            <h5 className="mb-1">{id ? 'Edit' : 'New'} {modelLabels.singular}</h5>
            <h6 className="text-muted mb-0">{id ? `Edit existing ${modelLabels.singular}.` : `Register new ${modelLabels.singular} below.`}</h6>
          </Col>
        </Row>

        {renderAsyncContent()}
      </Card>
    </Container>
  );
}